/* eslint-disable */
import React, { useEffect, useRef, useState } from "react";
import MDBox from "components/MDBox";
import { Button, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";

// Custom Components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import LeadBayForm from "examples/modal/LeadBayForm";
import BasicButton from "examples/NewDesign/CustomButton/BasicButton";
import LeadBayListing from "layouts/loanForm/loanData";

// Constant
import Constants, { Colors, Icons, defaultData } from "utils/Constants";

// Redux component
import { useLocation, useNavigate } from "react-router-dom";
import loanConfigThunk, { uploadInventoryFromExcel } from "redux/Thunks/LoanFormConfig";
import { useDispatch, useSelector } from "react-redux";
import { openSnackbar } from "redux/Slice/Notification";
import pxToRem from "assets/theme/functions/pxToRem";

const findScreenIndexForLoanType = (screens, loanType) => {
  if (!screens || !screens.length || !loanType) return 0;
  const normalized = String(loanType).toLowerCase().replace(/\s+/g, "").replace(/loan/g, "");

  const map = {
    home: defaultData?.HOME_LOAN_SCREEN_ID || "homeLoan_01",
    personal: defaultData?.PERSONAL_LOAN_SCREEN_ID || "personalLoan_02",
    business: defaultData?.BUSINESS_LOAN_SCREEN_ID || "businessLoan_04",
    car: defaultData?.CAR_LOAN_SCREEN_ID || "carLoan_03",
    credit: defaultData?.CREDIT_CARD_SCREEN_ID || "creditCard_05",
    creditcard: defaultData?.CREDIT_CARD_SCREEN_ID || "creditCard_05",
    education: defaultData?.EDUCATION_LOAN_SCREEN_ID || "educationLoan_06",
    loanagainstproperty: defaultData?.LOAN_AGAINST_PROPERTY_SCREEN_ID || "loanAgainstProperty_07",
    loanagainstsecurity: defaultData?.LOAN_AGAINST_SECURITY_SCREEN_ID || "loanAgainstSecurity_08",
    workingcapital: defaultData?.WORKING_CAPITAL_SCREEN_ID || "workingCapital_09",
    machinery: defaultData?.MACHINERY_LOAN_SCREEN_ID || "machineryLoan_10",
    gold: defaultData?.GOLD_LOAN_SCREEN_ID || "goldLoan_11",
  };

  const targetScreenId = map[normalized];
  if (targetScreenId) {
    const idx = screens.findIndex((s) => s.id === targetScreenId);
    if (idx !== -1) return idx;
  }

  const nameIdx = screens.findIndex((s) => {
    const sName = (s.name || "").toLowerCase().replace(/\s+/g, "").replace(/loan/g, "");
    return sName.includes(normalized) || normalized.includes(sName);
  });
  return nameIdx !== -1 ? nameIdx : 0;
};

function ProfileForm() {
  const location = useLocation();
  const navigate = useNavigate();
  const [prefillData, setPrefillData] = useState(null);
  const pendingPrefillRef = useRef(location.state?.prefillData ? { ...location.state } : null);

  const [open, setOpen] = useState({
    open: false,
    screenIndex: 0,
    cardType: "Incident",
    bgColor: "#043b9f",
  });

  const loanSlice = useSelector((state) => state.loan);

  const inputFileRef = useRef(null);
  const dispatch = useDispatch();
  const [loanMenuAnchor, setLoanMenuAnchor] = useState(null);
  const isLoanMenuOpen = Boolean(loanMenuAnchor);

  const handleLoanForm = (formIndex) => {
    setLoanMenuAnchor(null);
    setOpen({
      open: true,
      screenIndex: formIndex,
      cardType: "Incident",
      bgColor: "#043b9f",
    });
  };

  const handleReset = () => {
    sessionStorage.removeItem("finvibes_active_inquiry_id");
    sessionStorage.removeItem("finvibes_active_inquiry_loan_type");
    setPrefillData(null);
    setOpen({
      open: false,
      screenIndex: 0,
      cardType: "Incident",
      bgColor: "#043b9f",
    });
  };

  useEffect(() => {
    if (location.state?.prefillData) {
      pendingPrefillRef.current = { ...location.state };
    }
  }, [location.state]);

  useEffect(() => {
    const prefillState = pendingPrefillRef.current;
    if (!prefillState?.prefillData) return;

    const hasScreensConfig = loanSlice.screens?.some(
      (s) =>
        s.screensInfo?.properties &&
        Array.isArray(s.screensInfo.properties) &&
        s.screensInfo.properties.length > 0
    );

    if (hasScreensConfig) {
      const targetLoanType = prefillState.loanType || prefillState.prefillData.loanType;
      const targetIndex = findScreenIndexForLoanType(loanSlice.screens, targetLoanType);

      setPrefillData(prefillState.prefillData);
      setOpen({
        open: true,
        screenIndex: targetIndex,
        cardType: "Incident",
        bgColor: "#043b9f",
      });

      pendingPrefillRef.current = null;
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, loanSlice.screens, navigate]);

  useEffect(() => {
    (async () => {
      const res = await dispatch(loanConfigThunk());
      if (res.payload.status !== 200) {
        dispatch(
          openSnackbar({
            message: Constants.MOBILE_USERS_ERROR,
            notificationType: Constants.NOTIFICATION_ERROR,
          })
        );
      }
    })();
  }, []);

  const handleButtonClick = () => {
    if (inputFileRef.current) {
      inputFileRef.current.click(); // Trigger file input click
    }
  };

  const handleLoanMenuOpen = (event) => {
    setLoanMenuAnchor(event.currentTarget);
  };

  const handleLoanMenuClose = () => {
    setLoanMenuAnchor(null);
  };

  const onSubmitHandle = async (e) => {
    try {
      const file = e.target.files[0];

      const response = await dispatch(uploadInventoryFromExcel(file));
      if (response.payload.status === 201) {
        // Assuming successful upload returns status code 201
        dispatch(
          openSnackbar({
            message: Constants.UPLOAD_SUCCESS_MESSAGE,
            notificationType: Constants.NOTIFICATION_SUCCESS,
          })
        );
      }
    } catch (error) {
      console.error("Error:", error);
      // Handle error
    }
    inputFileRef.current.value = ""; // Reset the file input
  };

  const leadActions = (
    <>
      <Button
        id="choose-loan-button"
        aria-controls={isLoanMenuOpen ? "choose-loan-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={isLoanMenuOpen ? "true" : undefined}
        onClick={handleLoanMenuOpen}
        startIcon={<AccountBalanceOutlinedIcon fontSize="small" sx={{ color: "#475467" }} />}
        endIcon={Icons.DROPDOWN()}
        sx={{
          height: pxToRem(42),
          px: pxToRem(14),
          backgroundColor: Colors.WHITE,
          color: Colors.BLACK,
          border: "1px solid #EAECF0",
          borderRadius: pxToRem(6),
          boxShadow: "none",
          fontSize: pxToRem(13),
          fontWeight: 500,
          textTransform: "none",
          "&:hover": {
            backgroundColor: "#F9FAFB",
            boxShadow: "none",
          },
        }}
      >
        Choose Loan
      </Button>
      <Menu
        id="choose-loan-menu"
        anchorEl={loanMenuAnchor}
        open={isLoanMenuOpen}
        onClose={handleLoanMenuClose}
        MenuListProps={{ "aria-labelledby": "choose-loan-button" }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: pxToRem(230),
            maxHeight: pxToRem(420),
            borderRadius: pxToRem(8),
            border: "1px solid #EAECF0",
            boxShadow: "0px 12px 24px rgba(16, 24, 40, 0.12)",
          },
        }}
      >
        {loanSlice.screens.map((screen, index) => (
          <MenuItem
            key={screen.id || screen.name}
            onClick={() => handleLoanForm(index)}
            sx={{
              minHeight: pxToRem(36),
              px: pxToRem(12),
              gap: 1,
              fontSize: pxToRem(13),
              color: "#344054",
            }}
          >
            <ListItemIcon sx={{ minWidth: `${pxToRem(26)} !important` }}>
              {screen.suffixIcon}
            </ListItemIcon>
            <ListItemText
              primary={screen.name}
              primaryTypographyProps={{ fontSize: pxToRem(13), fontWeight: 500 }}
            />
            {Icons.ADD2}
          </MenuItem>
        ))}
      </Menu>
      <BasicButton
        title="Upload File"
        icon={Icons.EXPORT}
        background={Colors.WHITE}
        color={Colors.BLACK}
        action={handleButtonClick}
        ml={pxToRem(0)}
      />
      <input
        type="file"
        name="excelFile"
        accept=".xlsx, .xls"
        onChange={onSubmitHandle}
        ref={inputFileRef}
        style={{ display: "none" }} // Initially hide the file input
        required
        fullWidth
      />
    </>
  );

  return (
    <DashboardLayout>
      {/* <PageTitle title={PageTitles.LEAD_BAY} /> */}
      {/* Lead bay listing */}
      <LeadBayListing leadActions={leadActions} />
      {open.open && (
        <LeadBayForm
          screenIndex={open.screenIndex}
          cardType={open.cardType}
          open={open.open}
          setOpen={setOpen}
          bgColor={open.bgColor}
          handleReset={handleReset}
          prefillData={prefillData}
        />
      )}
    </DashboardLayout>
  );
}

export default ProfileForm;
